// Analytika → Přehled → Živě (22. 9. 2026, mockup „Živě v Analytice", varianta B).
//
// Kdo je teď na fitway40.cz, na které stránce a co dělá. Data z /api/web-live
// (stejné jako Web → Živě), obnova každých 15 s, když je okno vidět.
// Bez jmen a IP, návštěvnice se rozliší jen anonymním denním otiskem.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Users, Activity, BookOpen, ShoppingCart, MousePointerClick, FileText, BadgeCheck, LogOut, ListChecks, Send,
  Megaphone, Search, Link2, ThumbsUp, Mail, Play, Globe, MapPin,
} from "lucide-react";
import { fetchApi } from "../../lib/api.js";
import { ton as tonPodleKlice } from "../../lib/tony.js";
import { Dlazdice, ErrorNote, Stitek, EmptyState } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../../components/PrehledDlazdice.jsx";
import { cislo, kc, tvar, POPISY_SEKCI } from "./spolecne.jsx";

const OBNOVA_MS = 15000;

// Čitelné názvy stránek webu. Neznámá stránka se ukáže jako adresa.
const STRANKY = {
  "/": "Domovská stránka", "/bootcampy/": "Bootcampy", "/bootcampy/informace/": "Bootcampy, informace",
  "/bootcampy-nakup/": "Pokladna bootcampů", "/bootcampy-nakup/kbely/": "Pokladna Kbely", "/bootcampy-nakup/letna/": "Pokladna Letná",
  "/bootcampy-dekujeme/": "Po nákupu bootcampu", "/trenuj-se-mnou/": "Trénuj se mnou", "/chci-zkusebni-trenink/": "Dotazník z reklam", "/dekujeme/": "Odeslaný dotazník",
  "/dotaznik/": "Dotazník", "/jsz/": "Jídelníček spokojené ženy", "/jsz-sleva/": "JSŽ, sleva", "/jsz-nakup/": "Pokladna JSŽ",
  "/jsz-sleva-nakup/": "Pokladna JSŽ, sleva", "/dekujeme-za-nakup/": "Po nákupu JSŽ", "/jidelnicek-na-miru/": "Jídelníček na míru",
  "/dekujeme-jidelnicek/": "Po objednávce jídelníčku", "/klub/": "Klub", "/klub-nakup/": "Pokladna klubu", "/klub-dekujeme/": "Po nákupu klubu", "/kviz/": "Kvíz", "/kviz/dekuji/": "Kvíz odeslán", "/kviz/potvrzeno/": "Kvíz potvrzen",
  "/cenik/": "Ceník", "/kontakt/": "Kontakt", "/o-nas/": "O nás", "/recenze/": "Recenze", "/promeny/": "Proměny", "/blog/": "Blog",
  "/osobni-trener-praha/": "Osobní trenér Praha", "/osobni-trener-praha-9-kbely/": "Osobní trenér Kbely",
  "/online-vyzivovy-koucink/": "Online výživový koučink", "/vyzivove-poradenstvi-praha/": "Výživové poradenství",
  "/vyzivovy-koucink-praha/": "Výživový koučink", "/firemni-programy/": "Firemní programy",
};
// Lokalita bootcampu z měření (23. 9. 2026): u stránky, která je pro obě
// lokality, ať je hned vidět, kterou si žena vybrala.
const LOKALITY = { kbely: "Kbely", letna: "Letná" };
const VICE_LOKALIT = new Set(["/bootcampy/", "/bootcampy/informace/", "/bootcampy-nakup/", "/bootcampy-dekujeme/"]);

/** Lokalita, kterou má smysl ukázat: jen u stránek společných pro obě. */
export function lokalitaStranky(p, lokalita) {
  return VICE_LOKALIT.has(String(p || "")) ? LOKALITY[String(lokalita || "").toLowerCase()] || null : null;
}

export function nazevStranky(p) {
  const cesta = String(p || "");
  if (STRANKY[cesta]) return STRANKY[cesta];
  if (cesta.startsWith("/blog/")) {
    const slug = cesta.replace(/^\/blog\//, "").replace(/\/$/, "").replace(/-/g, " ");
    return slug ? `Blog, ${slug}` : "Blog";
  }
  return cesta || "neznámá stránka";
}
const jePokladna = (p) => /^\/(bootcampy-nakup|jsz-nakup|jsz-sleva-nakup|klub-nakup)\//.test(String(p || ""));
const sekce = (s) => POPISY_SEKCI[s] || s;

// Ikona zdroje: placená reklama má megafon, vyhledávače lupu (23. 9. 2026).
const IKONA_ZDROJE = {
  "meta-reklama": Megaphone, "google-ads": Megaphone, "meta-organik": ThumbsUp,
  "google-organik": Search, seznam: Search, vyhledavace: Search,
  email: Mail, youtube: Play, primy: Link2, ostatni: Globe,
};

/** Co návštěvnice právě dělá, podle její poslední události. */
function cinnost(typ, path, section, popis) {
  if (typ === "purchase") return { text: "zaplatila", ton: "ok", ikona: BadgeCheck, stitek: "zaplatila" };
  if (typ === "dotaznik") return { text: "odeslala dotazník", ton: "ok", ikona: Send, stitek: "odeslala" };
  if (typ === "click") return { text: popis ? `klikla na ${popis}` : "klikla k objednávce", ton: "pozor", ikona: MousePointerClick, stitek: "klikla" };
  if (typ === "krok") {
    // Kroky posílá kvíz, oba dotazníky (od 7. 10. 2026) i rezervace zkušebního tréninku.
    const p = String(path || "");
    if (p.startsWith("/kviz")) return { text: "vyplňuje kvíz", ton: "znacka", ikona: ListChecks, stitek: "kvíz" };
    if (p.startsWith("/dotaznik") || p.startsWith("/chci-zkusebni-trenink")) return { text: "vyplňuje dotazník", ton: "znacka", ikona: ListChecks, stitek: "dotazník" };
    return { text: "postupuje objednávkou", ton: "znacka", ikona: ListChecks, stitek: "krok" };
  }
  if (typ === "exit") return { text: "odešla ze stránky", ton: "neutralni", ikona: LogOut, stitek: "odešla" };
  if (typ === "section" && section) return { text: `čte sekci ${sekce(section)}`, ton: "znacka", ikona: BookOpen, stitek: "čte" };
  if (jePokladna(path)) return { text: "je na pokladně", ton: "zlata", ikona: ShoppingCart, stitek: "na pokladně" };
  return { text: "otevřela stránku", ton: "neutralni", ikona: FileText, stitek: "otevřela" };
}

const pred = (s) => {
  const n = Math.max(0, Number(s) || 0);
  if (n < 60) return `${n} s`;
  if (n < 3600) return `${Math.floor(n / 60)} min`;
  return `${Math.floor(n / 3600)} h`;
};
const doba = (s) => {
  const n = Math.max(0, Number(s) || 0);
  if (n < 60) return `${n} s`;
  if (n < 3600) return `${Math.floor(n / 60)} min`;
  return `${Math.floor(n / 3600)} h ${Math.floor((n % 3600) / 60)} min`;
};
const zarizeni = (z) => ({ mobil: "telefon" }[z] || z || "neznámo");

/** Živá data. `aktivni` = false nic nenačítá (Přehled je v režimu Za období). */
export function useZivaData(aktivni) {
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [nacita, setNacita] = useState(false);
  const bezi = useRef(false);

  const nacti = useCallback(async () => {
    setNacita(true);
    try {
      const j = await fetchApi("/api/web-live?sekce=1");
      if (!bezi.current) return;
      setData(j);
      setChyba(null);
    } catch (e) {
      if (bezi.current) setChyba(e.message);
    } finally {
      if (bezi.current) setNacita(false);
    }
  }, []);

  useEffect(() => {
    if (!aktivni) return undefined;
    bezi.current = true;
    nacti();
    const id = setInterval(() => { if (!document.hidden) nacti(); }, OBNOVA_MS);
    const naKartu = () => { if (!document.hidden) nacti(); };
    document.addEventListener("visibilitychange", naKartu);
    return () => {
      bezi.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", naKartu);
    };
  }, [aktivni, nacti]);

  return { data, chyba, nacita, nacti };
}

function Clovek({ l }) {
  const c = cinnost(l.type, l.path, l.section, l.popis);
  const t = tonPodleKlice(c.ton);
  const Ikona = c.ikona;
  return (
    <div className="polozka">
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.pilulka}`}>
          <Ikona className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 truncate text-base font-bold">{nazevStranky(l.path)}</span>
            <span className={`shrink-0 text-xs font-semibold tabular-nums ${l.ticho_s < 60 ? "text-emerald-600" : "text-slate-500"}`}>
              před {pred(l.ticho_s)}
            </span>
          </div>
          <div className="text-sm text-slate-600">{c.text}</div>
          {l.placene && (
            <div className="mt-0.5 text-[13px] text-slate-500">
              {l.kampan ? `kampaň ${l.kampan}${l.sada ? `, sada ${l.sada}` : ""}` : "kampaň neznámá, odkaz nemá utm"}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {lokalitaStranky(l.path, l.lokalita) && (
              <Stitek ton="znacka" velikost="mala" ikona={MapPin}>{lokalitaStranky(l.path, l.lokalita)}</Stitek>
            )}
            <Stitek ton={l.placene ? "zlata" : "neutralni"} velikost="mala" ikona={IKONA_ZDROJE[l.zdrojKlic] || Globe}>
              {l.skupina || l.zdroj}
            </Stitek>
            <Pilulka>{zarizeni(l.zarizeni)}</Pilulka>
            <Pilulka>{l.stranek} {tvar(l.stranek, "stránka", "stránky", "stránek")}</Pilulka>
            <Pilulka>na webu {doba(l.na_webu_s)}</Pilulka>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ZivyPrehled({ data, chyba }) {
  const s = data?.souhrn || {};
  const lide = data?.lide || [];
  const udalosti = data?.udalosti || [];
  // „Právě teď" = aktivní v posledních 5 minutách, na pokladně podle poslední stránky.
  const naPokladne = lide.filter((l) => jePokladna(l.path) && l.type !== "exit").length;
  // Kolik lidí na webu je z placené reklamy, s rozpadem podle platformy.
  const zReklamy = lide.filter((l) => l.placene);
  const zMety = zReklamy.filter((l) => l.zdrojKlic === "meta-reklama").length;
  const zGooglu = zReklamy.filter((l) => l.zdrojKlic === "google-ads").length;

  return (
    <>
      {chyba && <ErrorNote>Živá data se nepodařilo načíst: {chyba}</ErrorNote>}

      <KartaPrehledu>
        <div className="flex items-center gap-3 p-3 sm:p-4">
          <span className="relative flex h-3.5 w-3.5 shrink-0">
            <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${s.ted > 0 ? "animate-ping opacity-75" : "opacity-0"}`} />
            <span className={`relative inline-flex h-3.5 w-3.5 rounded-full ${s.ted > 0 ? "bg-emerald-500" : "bg-slate-300"}`} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">{cislo(s.ted ?? 0)}</span>
              <span className="text-base text-slate-600">{tvar(s.ted, "návštěvnice", "návštěvnice", "návštěvnic")} právě teď</span>
            </div>
            <div className="text-sm text-slate-500">za hodinu {cislo(s.hodina ?? 0)}, obnovuje se samo každých 15 s</div>
          </div>
        </div>
      </KartaPrehledu>

      <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
        <Dlazdice popisek="Dnes návštěvnice" hodnota={cislo(s.dnes ?? 0)} ton="znacka" />
        <Dlazdice
          popisek="Teď z reklamy"
          hodnota={cislo(zReklamy.length)}
          ton="zlata"
          doplnky={zReklamy.length > 0 && <Pilulka>{`Meta ${zMety} | Google ${zGooglu}`}</Pilulka>}
        />
        <Dlazdice popisek="Dnes nákupy" hodnota={cislo(s.nakupy ?? 0)} ton="ok" doplnky={s.trzba > 0 && <Pilulka>{kc(s.trzba)}</Pilulka>} />
        <Dlazdice popisek="Na pokladně teď" hodnota={cislo(naPokladne)} ton="zlata" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <KartaPrehledu>
          <HlavickaDlazdice ikona={Users} nazev="Kdo je na webu a co dělá" odznak={String(lide.length)} />
          {lide.length ? (
            <div className="space-y-2 p-3 sm:p-4">
              {lide.map((l, i) => <Clovek key={`${l.path}-${i}`} l={l} />)}
            </div>
          ) : (
            <EmptyState icon={Users} title="Právě teď na webu nikdo není" hint="Kdo za posledních 5 minut něco udělá, objeví se tu." />
          )}
        </KartaPrehledu>

        <KartaPrehledu>
          <HlavickaDlazdice ikona={Activity} nazev="Co se právě děje" podtitulek="Poslední hodina, nejnovější nahoře" />
          {udalosti.length ? (
            <div className="divide-y divide-slate-200 px-4 sm:px-5">
              {udalosti.map((u, i) => {
                const c = cinnost(u.type, u.path, u.section, u.popis);
                const lok = lokalitaStranky(u.path, u.lokalita);
                const kde = lok ? `${nazevStranky(u.path)}, ${lok}` : nazevStranky(u.path);
                const co = u.type === "purchase" && u.value != null
                  ? `${kc(u.value)}, ${kde}`
                  : u.type === "click" && u.popis ? `${u.popis}, ${kde}`
                  : u.type === "section" && u.section ? `${kde}, ${sekce(u.section)}`
                  : u.type === "krok" && u.krok != null
                    ? `${kde}, krok ${u.krok}${u.celkem ? `/${u.celkem}` : ""}${u.section ? `: ${u.section}` : ""}`
                  : kde;
                return (
                  <div key={i} className="flex items-center gap-3 py-2.5 text-sm">
                    <span className="w-11 shrink-0 tabular-nums text-slate-500">{String(u.cas || "").slice(0, 5)}</span>
                    <Stitek ton={c.ton} velikost="mala">{c.stitek}</Stitek>
                    <span className="min-w-0 leading-snug text-slate-800">{co}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Activity} title="Za poslední hodinu se nic nestalo" />
          )}
        </KartaPrehledu>
      </div>
    </>
  );
}
